import { format, parse, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, isSameDay } from 'date-fns';
import { pl } from 'date-fns/locale';

/** Zwraca YYYY-MM-DD w strefie lokalnej (unikaj toISOString - może przesunąć o dzień w UTC). */
export const toDateOnlyLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Dzien dzisiaj jako YYYY-MM-DD w strefie lokalnej. */
export const getTodayLocal = (): string => toDateOnlyLocal(new Date());

/** Czy lek jest obecnie aktywny na podstawie dat (bez checkboxa).
 * - permanent bez endDate = zawsze aktywny
 * - permanent z endDate = aktywny gdy dzisiaj <= endDate
 * - temporary = aktywny gdy dzisiaj między startDate a endDate
 * - tapering = aktywny gdy dzisiaj między min(schedule.startDate) a max(schedule.endDate)
 * - one-time = aktywny tylko w dniu oneTimeDate (i bez endDate = nie zakończony ręcznie)
 */
export const isMedicationActive = (medication: {
  type: string;
  startDate?: string;
  endDate?: string;
  oneTimeDate?: string;
  taperingSchedules?: { startDate: string; endDate?: string }[];
}, todayOverride?: string): boolean => {
  const today = todayOverride || getTodayLocal();
  if (!medication.startDate) return false;

  switch (medication.type) {
    case 'permanent':
      if (!medication.endDate) return true;
      return today <= medication.endDate;
    case 'temporary':
      if (!medication.endDate) return today >= medication.startDate;
      return today >= medication.startDate && today <= medication.endDate;
    case 'tapering':
      if (!medication.taperingSchedules?.length) return today >= medication.startDate;
      const starts = medication.taperingSchedules.map(s => s.startDate);
      const ends = medication.taperingSchedules.map(s => s.endDate || getTodayLocal());
      const rangeStart = starts.sort()[0];
      const rangeEnd = ends.sort().reverse()[0];
      return today >= rangeStart && today <= rangeEnd;
    case 'one-time':
      if (!medication.oneTimeDate) return false;
      if (medication.endDate) return false; // ręcznie zakończony
      return today === medication.oneTimeDate;
    default:
      return false;
  }
};

export const formatDate = (date: Date | string, formatStr: string = 'yyyy-MM-dd'): string => {
  const dateObj = typeof date === 'string' ? parse(date, 'yyyy-MM-dd', new Date()) : date;
  return format(dateObj, formatStr, { locale: pl });
};

export const formatDateTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'dd.MM.yyyy HH:mm', { locale: pl });
};

export const getWeekRange = (date: Date = new Date()) => {
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 })
  };
};

export const getMonthRange = (date: Date = new Date()) => {
  return {
    start: startOfMonth(date),
    end: endOfMonth(date)
  };
};

export const isToday = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? parse(date, 'yyyy-MM-dd', new Date()) : date;
  return isSameDay(dateObj, new Date());
};

export const getDaysArray = (startDate: Date, endDate: Date): Date[] => {
  const days: Date[] = [];
  let currentDate = startDate;
  while (currentDate <= endDate) {
    days.push(currentDate);
    currentDate = addDays(currentDate, 1);
  }
  return days;
};
